import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout, Menu, Typography, Spin, Empty } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import { DocsService, type DocSectionRef, type DocPageData, type DocPageContentBlock } from '@/services/docs.service';
import { getUploadsBaseUrl, normalizeUploadImageUrl } from '@/config/api.config';
import styled from 'styled-components';

const { Sider, Content } = Layout;
const { Title } = Typography;

const DocsLayout = styled(Layout)`
  min-height: calc(100vh - 64px - 70px);
  background: #fff;
`;

const Sidebar = styled(Sider)`
  background: #fafafa !important;
  padding: 16px 0;
  .ant-menu {
    background: transparent !important;
    border: none !important;
  }
`;

const DocContent = styled(Content)`
  padding: 24px 40px 48px;
  max-width: 800px;
  margin: 0 auto;

  .doc-block-text {
    margin-bottom: 1.25em;
    line-height: 1.7;
  }
  .doc-block-text p {
    margin-bottom: 0.75em;
  }
  .doc-block-image {
    margin: 1em 0;
  }
  .doc-block-image.img-left,
  .doc-block-image.img-right {
    max-width: 45%;
    display: inline-block;
    vertical-align: top;
    margin: 0 1em 1em 0;
  }
  .doc-block-image.img-right {
    margin: 0 0 1em 1em;
    float: right;
  }
  .doc-block-image.img-full {
    width: 100%;
    text-align: center;
  }
  .doc-block-image img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  }
  .doc-block-image figcaption {
    font-size: 12px;
    color: #888;
    margin-top: 4px;
  }
`;

function parseContent(content: string): DocPageContentBlock[] {
  try {
    const parsed = JSON.parse(content || '{}');
    return Array.isArray(parsed.blocks) ? parsed.blocks : [];
  } catch {
    return [];
  }
}

function DocBlock({ block }: { block: DocPageContentBlock }) {
  if (block.type === 'image' && block.imageUrl) {
    const pos = block.imagePosition || 'full';
    const raw = block.imageUrl.startsWith('http') ? block.imageUrl : `${getUploadsBaseUrl()}${block.imageUrl.startsWith('/') ? block.imageUrl : '/' + block.imageUrl}`;
    const src = normalizeUploadImageUrl(raw) ?? raw;
    return (
      <figure className={`doc-block-image img-${pos}`}>
        <img src={src} alt={block.imageAlt || ''} />
        {block.imageAlt && <figcaption>{block.imageAlt}</figcaption>}
      </figure>
    );
  }
  if (block.type === 'text' && block.content) {
    return (
      <div
        className="doc-block-text"
        dangerouslySetInnerHTML={{ __html: block.content }}
      />
    );
  }
  return null;
}

export const DocsPage: React.FC = () => {
  const { sectionSlug, pageSlug } = useParams<{ sectionSlug?: string; pageSlug?: string }>();
  const navigate = useNavigate();
  const [sections, setSections] = useState<DocSectionRef[]>([]);
  const [page, setPage] = useState<DocPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);

  useEffect(() => {
    DocsService.list()
      .then(setSections)
      .catch(() => setSections([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!sectionSlug || !pageSlug) {
      setPage(null);
      return;
    }
    setPageLoading(true);
    DocsService.getPage(sectionSlug, pageSlug)
      .then(setPage)
      .catch(() => setPage(null))
      .finally(() => setPageLoading(false));
  }, [sectionSlug, pageSlug]);

  const firstSection = sections[0];
  const firstPage = firstSection?.pages?.[0];
  const defaultPath = firstSection && firstPage
    ? `/docs/${firstSection.slug}/${firstPage.slug}`
    : null;

  useEffect(() => {
    if (!loading && !sectionSlug && defaultPath) {
      navigate(defaultPath, { replace: true });
    }
  }, [loading, sectionSlug, defaultPath, navigate]);

  const menuItems = sections.flatMap((sec) => [
    {
      key: `sec-${sec.id}`,
      type: 'group' as const,
      label: sec.title,
      children: (sec.pages || []).map((p) => ({
        key: `${sec.slug}-${p.slug}`,
        icon: <FileTextOutlined />,
        label: p.title,
        onClick: () => navigate(`/docs/${sec.slug}/${p.slug}`)
      }))
    }
  ]);

  const blocks = page ? parseContent(page.content) : [];

  return (
    <DocsLayout>
      <Sidebar width={260}>
        {loading ? (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <Spin />
          </div>
        ) : (
          <Menu
            mode="inline"
            selectedKeys={sectionSlug && pageSlug ? [`${sectionSlug}-${pageSlug}`] : []}
            defaultOpenKeys={sections.map((s) => `sec-${s.id}`)}
            items={menuItems}
            style={{ height: '100%' }}
          />
        )}
      </Sidebar>
      <DocContent>
        {pageLoading ? (
          <div style={{ padding: 48, textAlign: 'center' }}>
            <Spin size="large" />
          </div>
        ) : page ? (
          <>
            <Title level={2} style={{ marginBottom: 24 }}>
              {page.title}
            </Title>
            {blocks.length === 0 ? (
              <Empty description="Содержимое страницы пока не добавлено" />
            ) : (
              blocks.map((block, i) => <DocBlock key={i} block={block} />)
            )}
          </>
        ) : !sectionSlug && !defaultPath ? (
          <Empty description="Нет разделов документации" />
        ) : null}
      </DocContent>
    </DocsLayout>
  );
};

export default DocsPage;
